import React from 'react'
import styled from 'styled-components'

import { Layer, SelectionType } from 'modules/shares/types'
import { InspectorData } from 'modules/shares/Inspector/types'
import { CopyCSSButton } from 'modules/shares/ComponentsWebView/components/CopyCSSButton'
import ExportableIcon from 'modules/shares/Inspector/ExportableIcon'
import Section from '../Section'
import Attribute from '../Attribute'
import { isSymbolLayer } from '../../../Symbol'
import { ComponentName, ComponentSubName } from '../ComponentName'

import {
  splitSymbolNameAndPath,
  formatPath,
} from 'modules/shares/Inspector/Sidebar/utils'

import canvasDotted from '@sketch/icons/canvas-dotted-18.svg'
import artboardIconURL from '@sketch/icons/artboard-16.svg'
import textLayerIconURL from '@sketch/icons/text-format-16.svg'
import shapeLayerIconURL from '@sketch/icons/squares-2-16.svg'
import bitmapLayerIconURL from '@sketch/icons/image-16.svg'
import layerGroupIconURL from '@sketch/icons/folder-closed-16.svg'
import sliceIconURL from '@sketch/icons/square-knife-16.svg'
import symbolIconURL from '@sketch/icons/symbol-16.svg'

/**
 * TYPES
 */
export interface IconProps {
  selection: SelectionType
  className?: string
  hasExports: boolean
}

interface OverviewProps {
  artboardName?: string
  isSymbol?: boolean
  symbolDocumentName?: string
  inspectorData?: InspectorData
  layer?: Layer
  textStyles?: InspectorData['textStyles']
  layerStyles?: InspectorData['layerStyles']
}

/**
 * HELPERS
 */
const pageOrArtboard = (inspectorData?: InspectorData): 'page' | 'artboard' =>
  inspectorData?.frame?.width && inspectorData?.frame?.height
    ? 'artboard'
    : 'page'

const imagePropsForSelection = (selectionType: SelectionType) => {
  switch (selectionType) {
    case 'text':
      return {
        src: textLayerIconURL,
        alt: 'Text layer icon',
        width: '17px',
        height: '17px',
      }

    case 'polygon':
    case 'triangle':
    case 'star':
    case 'rectangle':
    case 'oval':
    case 'shapePath':
    case 'shapeGroup':
      return {
        src: shapeLayerIconURL,
        alt: 'Shape layer icon',
        width: '17px',
        height: '17px',
      }

    case 'bitmap':
      return {
        src: bitmapLayerIconURL,
        alt: 'Bitmap layer icon',
        width: '17px',
        height: '17px',
      }

    case 'group':
      return {
        src: layerGroupIconURL,
        alt: 'Layer group icon',
        width: '17px',
        height: '17px',
      }

    case 'artboard':
      return {
        src: artboardIconURL,
        alt: 'Artboard icon',
        width: '20px',
        height: '18px',
      }

    case 'slice':
      return {
        src: sliceIconURL,
        alt: 'Slice icon',
        width: '17px',
        height: '17px',
      }

    case 'symbol':
      return {
        src: symbolIconURL,
        alt: 'Symbol icon',
        width: '17px',
        height: '17px',
      }

    default:
    case 'page':
      return {
        src: canvasDotted,
        alt: 'Page icon',
        width: '18px',
        height: '18px',
      }
  }
}

// Some icons need an extra space for the exportable icon (the knife icon)
const isNeededExtraSpaceForExportableIcon = (
  selection: SelectionType
): boolean =>
  selection === 'artboard' || selection === 'text' || selection === 'group'

/**
 * STYLES
 */
const ExportableIconWrapper = styled.div<{ selection: SelectionType }>`
  width: 21px;
  height: 21px;
  position: absolute;
  transform: ${({ selection }) =>
    isNeededExtraSpaceForExportableIcon(selection)
      ? 'translate(2px, 1px)'
      : undefined};
`

const BaseIcon = ({ selection, hasExports, className }: IconProps) => (
  <div className={className}>
    {/* Slice already has an exportable icon present on the icon */}
    {hasExports && selection !== 'slice' && (
      <ExportableIconWrapper selection={selection}>
        <ExportableIcon maskColor="#F0F0F0" />
      </ExportableIconWrapper>
    )}
    {/* eslint-disable-next-line jsx-a11y/alt-text */}
    <img {...imagePropsForSelection(selection)} />
  </div>
)

export const Icon = styled(BaseIcon)`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: none;

  width: 36px;
  height: 36px;

  background-color: #f0f0f0;
  border-radius: 4px;
  margin-right: 12px;
`

const OverlaySection = styled(Section)`
  width: 100%;
`

const OverlayAttribute = styled(Attribute)`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  padding-right: 0;
`

const OverviewComponentName = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: 12px;
`

const OverviewCopyCSSButton = styled(CopyCSSButton)`
  flex: none;
`

/**
 * MAIN COMPONENT
 * Overview info, used for both layer and artboard/page info
 */
const Overview: React.FC<OverviewProps> = ({
  artboardName,
  isSymbol,
  symbolDocumentName,
  inspectorData,
  layer,
  textStyles,
  layerStyles,
}) => {
  if (!inspectorData && !layer) {
    return null
  }

  let hasExports = false
  if (layer) {
    hasExports = !!layer?.exportFormats?.length
  } else {
    hasExports = !!inspectorData?.exportFormats?.length
  }

  const layerType =
    isSymbolLayer(layer) || isSymbol
      ? 'symbol'
      : layer?.type || pageOrArtboard(inspectorData)

  const [symbolName, symbolPath] = splitSymbolNameAndPath(artboardName)

  const name = {
    layer: layer?.name,
    artboard: artboardName,
    symbol: symbolName,
  }

  const getOverviewType = (): keyof typeof name => {
    if (layer) return 'layer'
    if (isSymbol) {
      return 'symbol'
    }

    return 'artboard'
  }

  const type = getOverviewType()

  const documentName =
    symbolDocumentName === '' ? 'This Document' : symbolDocumentName

  const shouldShowCopyCSSButton = layer && layerType !== 'slice'

  return (
    <OverlaySection data-testid="inspector-sidebar-overview">
      <OverlayAttribute>
        <Icon selection={layerType} hasExports={hasExports} />
        <OverviewComponentName>
          <ComponentName>{name[type]}</ComponentName>
          {/* Note: type === 'symbol' is not the same as layerType === 'symbol'. This seems to only be shown
          when opening the artboard of a symbol*/}
          {type === 'symbol' && (
            <ComponentSubName>
              {formatPath([documentName || '', ...symbolPath])}
            </ComponentSubName>
          )}
        </OverviewComponentName>
        {shouldShowCopyCSSButton && (
          <OverviewCopyCSSButton
            style={{
              ...textStyles,
              ...layerStyles,
              ...layer.style,
              corners: layer.cornerRadius
                ? {
                    cornerRadius: layer.cornerRadius,
                  }
                : undefined,
            }}
          />
        )}
      </OverlayAttribute>
    </OverlaySection>
  )
}

export default Overview
